import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { ComboBox, DropDownList } from "@progress/kendo-react-dropdowns";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useEffect, useMemo, useRef, useState } from "react";
import { displayedTypes, getAllowedActionTypes, getShowAsOptions, getTargetPageOptionsForActionType, getUsedActionTypes, isUrlValid } from "./utils";
import { useSelectedComponent } from "../../../../Hooks/Display/useComponents";
import usePages, { useSelectedPage } from "../../../../Hooks/Display/usePages";
import { getPageByProperty, getPagesOfPortal } from "../../utils";
import { ALL_NAVIGATE_ACTION_TYPES, ATTRIBUTE_URL_ACTION, EDIT_ACTION, EXTERNAL_URL_ACTION, LINK_ACTION, NAVIGATE_ACTION, NAVIGATE_TO_PAGE_ACTION_TYPES, NEW_ACTION } from "./constants";
import { createAction } from "../../../../API/Display/Display";
import { useActions, useFetchedValues } from "../../../../Hooks/Display/useComponentProperties";
import IconPicker from "../../../Shared/IconPicker/IconPicker";

function ActionForm({ submitButtonText, initialValue = {}, onSubmit, onCancel }) {

    const [{ pages }] = usePages();
    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();

    const [actionsState, dispatch] = useActions();
    const [{ actionTable: { attributes }}] = useFetchedValues();
    const urlAttributes = attributes.filter(attr => attr.attributeType === "URL");

    const [state, setState] = useState({
        actionName: "",
        actionDisplay: "",
        actionType: "",
        showLabel: true,
        label: "",
        showIcon: false,
        icon: "",
        target: {},
        targetUrl: "",
        targetShowAs: "",
        tooltip: ""
    });

    const [actionTypes, setActionTypes] = useState([])

    const pagesOfPortal = useMemo(
        () => getPagesOfPortal(selectedPage.portalId, pages),
        []
    );
    const targetPages = useMemo(
        () => getTargetPageOptionsForActionType(
            pagesOfPortal, state.actionType, selectedComponent.componentType, selectedPage.useAsReference === "true"
        ), [state.actionType]
    )

    useEffect(() => {
        const usedActionTypes = getUsedActionTypes(actionsState.actions);
        const allowedActionTypes = getAllowedActionTypes(
            selectedComponent.componentType,
            selectedPage.subPageType,
            (selectedPage.useAsReference === "true" || selectedPage.useAsReference === true),
            usedActionTypes
        )
        if (initialValue && Object.keys(initialValue).length > 0 && initialValue.actionType !== "Button Group") {
            allowedActionTypes.push(initialValue.actionType);
        }
        setActionTypes([...new Set(allowedActionTypes)])
        setState(prevState => ({
            ...prevState,
            actionDisplay: displayedTypes[0],
            actionType: allowedActionTypes[0],
            targetShowAs: getShowAsOptions(allowedActionTypes[0])[0],
        }));
    }, [])

    useEffect(() => {
        if (initialValue && Object.keys(initialValue).length > 0) {
            const propertiesToSet = { ...initialValue };
            if (NAVIGATE_TO_PAGE_ACTION_TYPES.includes(initialValue.actionType)) {
                propertiesToSet.target = getPageByProperty(pages, "_id", initialValue.targetPageId);
            } else if (initialValue.actionType === ATTRIBUTE_URL_ACTION) {
                propertiesToSet.target = urlAttributes.find((attribute) => attribute.id === initialValue.targetAttributeId);
            }
            Object.keys(state).forEach(key => {
                if (propertiesToSet[key] === undefined) {
                    propertiesToSet[key] = initialValue[key];
                }
            })
            setState(prevState => ({
                ...prevState,
                ...propertiesToSet
            }))
        }
    }, [])

    function handleChange(event) {
        setState(prevState => ({
            ...prevState,
            [event.target.name]: event.target.value
        }));
    }

    function handleActionName(event) {
        const maxLength = event.target.maxLength;
        const stateToUpdate = {
            actionName: event.target.value.substring(0, maxLength)
        }
        if (initialValue && Object.keys(initialValue).length === 0) {
            stateToUpdate.tooltip = event.target.value.substring(0, maxLength);
            stateToUpdate.label = event.target.value.substring(0, maxLength);
        }
        setState(prevState => ({
            ...prevState,
            ...stateToUpdate
        }));
    }

    function handleActionDisplayChange(event) {
        const actionDisplay = event.target.value;
        const stateToUpdate = {
            actionDisplay: actionDisplay
        }
        if (actionDisplay === "Button") {
            stateToUpdate.actionType = actionTypes[0];
            stateToUpdate.targetShowAs = getShowAsOptions(actionTypes[0])[0];
        } else {
            stateToUpdate.actionType = "Button Group";
            stateToUpdate.targetShowAs = "";
        }
        setState(prevState => ({
            ...prevState,
            ...stateToUpdate
        }));
    }

    function handleIconChange(icon) {
        setState(prevState => ({
            ...prevState,
            icon: icon
        }));
    }

    function isSaveDisabled() {
        const isActionNameEmpty = state.actionName.trim() === "";
        if (isActionNameEmpty) {
            return true;
        }

        let isChanged = false;
        if (
            initialValue &&
            Object.keys(initialValue).length > 0
        ) {
            isChanged = (
                state.actionName !== initialValue.actionName ||
                state.actionDisplay !== initialValue.actionDisplay ||
                state.actionType !== initialValue.actionType ||
                state.showLabel !== initialValue.showLabel ||
                state.label !== initialValue.label ||
                state.showIcon !== initialValue.showIcon ||
                state.icon !== initialValue.icon ||
                state.targetUrl !== initialValue.targetUrl ||
                state.targetShowAs !== initialValue.targetShowAs ||
                state.tooltip !== initialValue.tooltip
            )
        } else {
            isChanged = true;
        }
    
        switch (state.actionDisplay) {
            case "Button":
                switch (state.actionType) {
                    case NAVIGATE_ACTION:
                    case EDIT_ACTION:
                    case NEW_ACTION:
                        // target empty
                        if (!state.target || !state.target._id) {
                            isChanged = false;
                            break;
                        }
                        if (initialValue) {
                            const targetChanged = state.target._id !== initialValue.targetPageId;
                            isChanged = isChanged || targetChanged;
                        }
                        break;
                    case ATTRIBUTE_URL_ACTION:
                        // target empty
                        if (!state.target || !state.target.id) {
                            isChanged = false;
                            break;
                        }
                        if (initialValue) {
                            isChanged = isChanged || (state.target.id !== initialValue.targetAttributeId);
                        }
                        break;
                    case EXTERNAL_URL_ACTION:
                        if (initialValue) {
                            if (!isUrlValid(state.targetUrl)) {
                                isChanged = false;
                            } else {
                                isChanged = isChanged || (
                                    state.targetUrl !== initialValue.targetUrl
                                )
                            }
                        }
                        break;
                    case LINK_ACTION:
                        isChanged = isChanged && (
                            selectedComponent.componentType === "Heading" &&
                            state.target._id
                        )
                        break;
                    default:
                        break;
                }
                break;
            default:
                break;
        }

        if (state.label.trim() === "") {
            isChanged = false;
        }

        return !isChanged;
    }

    async function handleSubmit(event) {
        event.preventDefault();
        const action = {
            actionName: state.actionName,
            actionDisplay: state.actionDisplay,
            actionType: state.actionType,
            showLabel: state.showLabel,
            label: state.label,
            showIcon: state.showIcon,
            icon: state.icon,
            targetUrl: state.targetUrl,
            targetShowAs: state.targetShowAs,
            tooltip: state.tooltip
        };
        if (NAVIGATE_TO_PAGE_ACTION_TYPES.includes(state.actionType)) {
            action.targetPageId = state.target._id;
        } else if (state.actionType === ATTRIBUTE_URL_ACTION) {
            action.targetAttributeId = state.target.id;
        }
        onSubmit(event, action);
    }

    return (
        <>
            <form className="container-fluid">
                <div className="row">
                    <div className="col-12 form-group">
                        <label className="form-label">Action Name</label>
                        <input
                            autoFocus={true}
                            name="actionName"
                            type="text"
                            className="form-control name-text"
                            maxLength={36}
                            value={state.actionName}
                            onChange={handleActionName}
                        />
                    </div>
                    <div className="col-12 form-group">
                        <label className="form-label">Display Type</label>
                        <DropDownList
                            name="actionDisplay"
                            value={state.actionDisplay}
                            onChange={handleActionDisplayChange}
                            data={displayedTypes}
                        />
                    </div>
                    {
                        state.actionDisplay === "Button" &&
                        <div className="col-12 form-group">
                            <label className="form-label">Action Type</label>
                            <DropDownList
                                name="actionType"
                                value={state.actionType}
                                onChange={handleChange}
                                data={actionTypes}
                            />
                        </div>
                    }
                    <div className="col-12 form-group">
                        <Checkbox
                            name="showLabel"
                            label={"Label"}
                            labelPlacement="after"
                            checked={state.showLabel}
                            onChange={handleChange}
                        />
                        {
                            state.showLabel &&
                            <input
                                name="label"
                                type="text"
                                className="form-control"
                                value={state.label}
                                onChange={handleChange}
                            />
                        }
                    </div>
                    <div className="col-12 form-group">
                        <Checkbox
                            name="showIcon"
                            label={"Icon"}
                            labelPlacement="after"
                            checked={state.showIcon}
                            onChange={handleChange}
                        />
                        {
                            state.showIcon &&
                            <IconPicker
                                isActionIcon={true}
                                selected={state.icon}
                                disabled={!state.showIcon}
                                onChange={handleIconChange} />
                        }
                    </div>
                    {
                        NAVIGATE_TO_PAGE_ACTION_TYPES.includes(state.actionType) &&
                        <div className="col-12 form-group">
                            <label className="form-label">Target Page</label>
                            <ComboBox
                                name="target"
                                value={state.target}
                                onChange={handleChange}
                                data={targetPages}
                                textField="pageName"
                                dataItemKey="_id"
                            />
                        </div>
                    }
                    {
                        state.actionType === EXTERNAL_URL_ACTION &&
                        <div className="col-12 form-group">
                            <label className="form-label">Target URL</label>
                            <input
                                name="targetUrl"
                                type="text"
                                value={state.targetUrl}
                                className="form-control"
                                onChange={handleChange}
                            />
                        </div>
                    }
                    {
                        state.actionType === ATTRIBUTE_URL_ACTION &&
                        <div className="col-12 form-group">
                            <label className="form-label">Target Attribute</label>
                            <ComboBox
                                name="target"
                                value={state.target}
                                onChange={handleChange}
                                data={urlAttributes}
                                textField="attributeName"
                                dataItemKey="id"
                            />
                        </div>
                    }
                    {
                        ALL_NAVIGATE_ACTION_TYPES.includes(state.actionType) &&
                        <div className="col-12 form-group">
                            <label className="form-label">Target Show As</label>
                            <ComboBox
                                name="targetShowAs"
                                value={state.targetShowAs}
                                onChange={handleChange}
                                data={getShowAsOptions(state.actionType)}
                            />
                        </div>
                    }
                    <div className="col-12 form-group">
                        <label className="form-label">Tooltip</label>
                        <input
                            name="tooltip"
                            type="text"
                            value={state.tooltip}
                            className="form-control"
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </form>
            <DialogActionsBar layout="end">
                <button
                    className="btn"
                    onClick={onCancel}
                >
                    Cancel
                </button>
                <button
                    className="btn btn-primary"
                    disabled={isSaveDisabled()}
                    onClick={handleSubmit}
                >
                    {submitButtonText}
                </button>
            </DialogActionsBar>
        </>
    )
}

export default ActionForm;